import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { School } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Bienvenido, ${u.name}`);
      navigate(u.role === "admin" ? "/admin" : "/docente", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      {/* Left: brand panel */}
      <div className="hidden lg:flex relative bg-[#10312B] text-white overflow-hidden">
        <div className="absolute top-0 left-0 right-0 flex h-1.5">
          <div className="flex-1 bg-[#235B4E]" />
          <div className="flex-1 bg-white" />
          <div className="flex-1 bg-[#9F2241]" />
        </div>
        <img
          src="https://images.unsplash.com/photo-1547686669-9a8cb1a22d91?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="relative z-10 p-14 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_edu-censo-admin/artifacts/8c23fucn_Logo%20Censo911.png"
              alt="Censo 911"
              className="w-14 h-14 object-contain bg-white rounded p-1 shrink-0"
              crossOrigin="anonymous"
            />
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-white/60">
                Sistema Censo 911
              </div>
              <div className="text-lg font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Sistema de Captura 911
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <h1 className="text-4xl lg:text-5xl leading-tight font-semibold tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Censo escolar<br />
              <span className="text-[#BC955C]">preescolar, primaria y secundaria.</span>
            </h1>
            <p className="text-sm text-white/70 max-w-md">
              Administre planteles, grupos y alumnado. Genere reportes oficiales listos para
              entrega a las autoridades educativas.
            </p>
          </div>
          <div className="text-xs text-white/50">Ciclo escolar 2026 – 2027</div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-2">Acceso</div>
            <h2 className="text-3xl font-semibold text-[#10312B]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Iniciar sesión
            </h2>
            <p className="text-sm text-[#545454] mt-1">
              Ingrese sus credenciales institucionales.
            </p>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Correo</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="correo@ejemplo.com"
                required
                autoComplete="email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />
            </div>
            <Button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-[#235B4E] hover:bg-[#1D4A40] text-white"
            >
              {loading ? "Ingresando…" : "Entrar"}
            </Button>
          </form>
          <div className="mt-8 text-xs text-[#9CA3AF] border-t border-[#E5E7EB] pt-4">
            Sistema oficial de captura. Todo acceso queda registrado.
          </div>
        </div>
      </div>
    </div>
  );
}
